/**
 * DOCUMENTATION:
 * Component: UiUxTypography
 * Purpose: Educational page detailing typographic hierarchy, readability metrics, and font implementation.
 * * * Note for Junior Developers:
 * - Typography is 90% of web design. If your text is hard to read, the user will leave immediately.
 * - Golden Rule of Readability: A line of text should never be longer than 60-75 characters.
 * - Never use pixels (px) for font sizes. Always use 'rem' so visually impaired users can scale the text via browser settings.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxTypography() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Typography Concepts
    const typographyConcepts = useMemo(() => [
        { concept: "Visual Hierarchy", application: "Navigation", description: "Using size, weight (boldness), and color to guide the user's eye. H1 is the most important; paragraphs are the baseline." },
        { concept: "Line Length (Measure)", application: "Readability", description: "Lines of text that are too long cause eye strain. Lines that are too short disrupt reading rhythm. Aim for 60-75 characters per line." },
        { concept: "Line Height (Leading)", application: "Breathing Room", description: "The vertical space between lines of text. Body text should typically have a line-height of 1.5 to 1.6 for comfortable reading." },
        { concept: "Font Pairing", application: "Brand Identity", description: "Combining two distinct fonts (e.g., a bold Sans-Serif for headings and a highly legible Serif for body text) creates visual interest without clutter." },
    ], []);

    // Coding examples demonstrating how Typography translates to CSS
    const codingModules = useMemo(() => [
        {
            title: "1. The Fluid Typographic Scale",
            code: `:root {\n  /* Fluid Type: Scales mathematically between min and max viewport widths */\n  --font-base: clamp(1rem, 1vw + 0.5rem, 1.125rem);\n  --font-h1: clamp(2.5rem, 5vw, 4rem);\n  --font-h2: clamp(2rem, 4vw, 3rem);\n}\n\nbody {\n  /* Never set this in pixels! Let the browser calculate it. */\n  font-size: var(--font-base);\n  \n  /* 1.6x the font size gives perfect breathing room for paragraphs */\n  line-height: 1.6;\n}\n\nh1 {\n  font-size: var(--font-h1);\n  /* Headings need TIGHTER line-heights than body text */\n  line-height: 1.1;\n  font-weight: 800;\n}`,
            desc: "Instead of writing dozens of media queries to shrink headings on mobile devices, use the CSS `clamp()` function. Also note that as text gets larger (Headings), the `line-height` should get tighter."
        },
        {
            title: "2. Enforcing Readability with the 'ch' Unit",
            code: `/* ❌ BAD UX: Text stretches edge-to-edge on 4K monitors */\n.article-body-bad {\n  width: 100%;\n}\n\n/* ✅ GOOD UX: Constrain reading width using characters */\n.article-body-good {\n  /* The 'ch' unit equals the width of the '0' character in the current font */\n  max-width: 65ch;\n  \n  /* Centers the article column horizontally */\n  margin-inline: auto;\n  \n  color: #333; /* Dark grey is often easier to read than pure #000 black */\n}`,
            desc: "The `ch` unit is a frontend developer's secret weapon for typography. It allows you to set the width of a text container based strictly on the number of characters, guaranteeing the perfect reading measure regardless of the font size."
        },
        {
            title: "3. System Fonts vs. Web Fonts",
            code: `/* Approach A: The Custom Web Font (e.g., Google Fonts) */\n@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');\n\n.brand-text {\n  font-family: 'Inter', sans-serif;\n  /* 'display: swap' prevents invisible text while the font downloads! */\n}\n\n/* Approach B: The Zero-Latency System Font Stack */\n.dashboard-ui {\n  /* Uses the native font of whatever OS the user is on (Apple, Windows, Android) */\n  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;\n}`,
            desc: "Custom fonts are beautiful but cost performance (download time). For complex UIs or dashboards, use the 'System Font Stack'. It loads instantly because the fonts are already installed on the user's machine."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    TYPOGRAPHY & READABILITY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Text as an Interface
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Web design is 95% typography. The way you size, space, and color your text dictates whether a user comprehends your application or becomes immediately frustrated. Creating a strict typographic scale ensures that your visual hierarchy makes sense—guiding the user from the massive H1 (what is this page?), down to the H2s (what are the sections?), and finally into the highly-legible body copy.
                        </Typography>

                        {/* ✅ Core Typography Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Typographic Engineering Rules</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Rule</div>
                                    <div>Application Focus</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {typographyConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.application}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
